/**
 * One video in a grid: poster frame, duration, status, title, meta.
 *
 * Shared between the Library page and the Studio picker so a video looks like
 * the same object everywhere. The card body is one click target (`onOpen`);
 * anything extra — a kebab menu, an Add button — arrives through `action` and
 * stops its own propagation.
 */
import { Clapperboard, Film, Link2, Loader2, MonitorPlay, Video } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  IMPORT_SOURCES,
  importSourceFor,
  type RecordingSummary,
} from "@rec/shared";
import { StatusBadge } from "@/components/RecordingStatus";
import { useFormat } from "@/i18n/format";
import { cn } from "@/lib/utils";

/** The platforms `common:platform` has a translated label for. */
const TRANSLATED_PLATFORMS = [
  "zoom",
  "google_meet",
  "teams",
  "upload",
  "studio",
  "youtube",
  "linkedin",
] as const;
type TranslatedPlatform = (typeof TRANSLATED_PLATFORMS)[number];

/**
 * A platform's label in the interface's language. Computed at render rather
 * than held in a module-level map, which could not follow a language change.
 */
export function platformLabel(t: TFunction<"common">, platform: string): string {
  if ((TRANSLATED_PLATFORMS as readonly string[]).includes(platform)) {
    return t(`platform.${platform as TranslatedPlatform}`);
  }
  // From the registry, so a platform added there is labelled here for free.
  return IMPORT_SOURCES.find((source) => source.id === platform)?.label ?? platform;
}

/** `platformLabel`, bound to the current language. */
export function usePlatformLabel(): (platform: string) => string {
  const { t } = useTranslation("common");
  return (platform) => platformLabel(t, platform);
}

function PlaceholderIcon({ platform }: { platform: string }) {
  const Icon =
    platform === "studio"
      ? Clapperboard
      : platform === "upload"
        ? Film
        : importSourceFor(platform)
          ? Link2
          : MonitorPlay;
  return <Icon size={26} strokeWidth={1.5} className="text-text-faint" />;
}

/** Stable muted hue per id, so placeholder cards are tellable apart. */
function cardHue(id: string): number {
  let hash = 0;
  for (const ch of id) hash = (hash * 31 + ch.charCodeAt(0)) | 0;
  return Math.abs(hash) % 360;
}

export function MediaCard({
  recording,
  onOpen,
  action,
  busy,
  className,
}: {
  recording: RecordingSummary;
  onOpen?: () => void;
  /** Rendered top-right over the thumbnail; must stopPropagation itself. */
  action?: React.ReactNode;
  /** Dim the card and show a spinner over the poster (e.g. mid-processing). */
  busy?: boolean;
  className?: string;
}) {
  const ready = recording.status === "ready";
  const f = useFormat();
  const label = usePlatformLabel();

  return (
    <div
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={(e) => {
        // Only when the card itself is focused. A menu or button rendered
        // into the `action` slot is a descendant, and Enter on *it* must run
        // its own action — not navigate the card out from under it.
        if (e.target !== e.currentTarget) return;
        if (onOpen && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onOpen();
        }
      }}
      className={cn(
        "group relative flex flex-col overflow-hidden rounded-xl border border-border bg-surface text-left transition-colors",
        onOpen && "cursor-pointer hover:border-border-strong hover:bg-surface-hover",
        className,
      )}
    >
      {/* Poster. A real frame when the worker has captured one; otherwise a
          tinted placeholder — never a broken-image icon. */}
      <div
        className="relative aspect-video w-full overflow-hidden bg-bg-subtle"
        style={
          recording.thumbnailUrl
            ? undefined
            : { background: `hsl(${cardHue(recording.id)} 18% 16%)` }
        }
      >
        {recording.thumbnailUrl ? (
          <img
            src={recording.thumbnailUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <PlaceholderIcon platform={recording.platform} />
          </div>
        )}

        {busy || !ready ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/45">
            {recording.status === "processing" || busy ? (
              <Loader2 size={20} className="animate-spin text-white/80" />
            ) : null}
          </div>
        ) : null}

        {recording.durationMs ? (
          <span className="absolute right-1.5 bottom-1.5 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[10.5px] tabular-nums text-white">
            {f.duration(recording.durationMs)}
          </span>
        ) : null}

        {!ready ? (
          <span className="absolute bottom-1.5 left-1.5">
            <StatusBadge recording={recording} />
          </span>
        ) : null}

        {action ? <span className="absolute top-1.5 right-1.5">{action}</span> : null}
      </div>

      <div className="min-w-0 p-2.5">
        <p dir="auto" className="line-clamp-2 text-[13px] font-medium leading-snug text-text">
          {recording.title}
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-[11.5px] text-text-faint">
          <Video size={11} className="shrink-0" />
          <span className="truncate">
            {label(recording.platform)}
            {" · "}
            {f.date(recording.startedAt, {
              day: "numeric",
              month: "short",
            })}
          </span>
        </p>
      </div>
    </div>
  );
}
